/* eslint import/no-webpack-loader-syntax: off */
import {useEffect, useRef} from 'react'
import mapboxgl from '!mapbox-gl'

import {useMapLayersV2} from '../../../../../../Map/MapLayersV2Context'
import {useMapLegendContext} from '../../../Helpers/MapLegend'
import {useViewImageContext} from '../../ViewImageContext'
import {viewImageState} from '../../ViewImageState'
import {formatMeters} from '../../../../../../../../shared/numberFormat'

const MAPBOX_TERRAIN_RGB_TOKEN =
  mapboxgl.accessToken ||
  'pk.eyJ1IjoiZ2lhbm90dGlmcmFuY28iLCJhIjoiY2w2ZHMzNjF4MDNuMjNpcDU1MHoxMmJ2OSJ9.-08oVGVdX82fh1cnhY40pQ'
const TERRAIN_TILE_SIZE = 256
const TERRAIN_ZOOM = 15
const MAX_RASTER_SIZE = 640

const clamp = (value, min, max) => Math.min(Math.max(value, min), max)

const lonToTileX = (lon, zoom) => ((lon + 180) / 360) * 2 ** zoom

const latToTileY = (lat, zoom) => {
  const rad = (lat * Math.PI) / 180
  return ((1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2) * 2 ** zoom
}

const tileXToLon = (tileX, zoom) => (tileX / 2 ** zoom) * 360 - 180

const tileYToLat = (tileY, zoom) => {
  const mercator = Math.PI * (1 - (2 * tileY) / 2 ** zoom)
  return (Math.atan(Math.sinh(mercator)) * 180) / Math.PI
}

const collectCoordinates = (coordinates, result = []) => {
  if (!Array.isArray(coordinates)) {
    return result
  }

  if (typeof coordinates[0] === 'number' && typeof coordinates[1] === 'number') {
    result.push(coordinates)
    return result
  }

  coordinates.forEach((entry) => collectCoordinates(entry, result))
  return result
}

const getGeometryBounds = (geometry) => {
  const points = collectCoordinates(geometry?.coordinates)

  if (points.length < 1) {
    return null
  }

  return points.reduce(
    (bounds, [lon, lat]) => ({
      west: Math.min(bounds.west, lon),
      south: Math.min(bounds.south, lat),
      east: Math.max(bounds.east, lon),
      north: Math.max(bounds.north, lat),
    }),
    {
      west: Infinity,
      south: Infinity,
      east: -Infinity,
      north: -Infinity,
    }
  )
}

const normalizePolygons = (geometry) => {
  if (geometry?.type === 'Polygon') {
    return [geometry.coordinates]
  }

  if (geometry?.type === 'MultiPolygon') {
    return geometry.coordinates
  }

  return []
}

const isPointInRing = ([lon, lat], ring) => {
  let inside = false

  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i]
    const [xj, yj] = ring[j]
    const intersects =
      (yi > lat) !== (yj > lat) && lon < ((xj - xi) * (lat - yi)) / (yj - yi) + xi

    if (intersects) {
      inside = !inside
    }
  }

  return inside
}

const isPointInGeometry = (point, geometry) => {
  const polygons = normalizePolygons(geometry)

  return polygons.some((rings) => {
    const [outerRing, ...holes] = rings

    if (!outerRing || !isPointInRing(point, outerRing)) {
      return false
    }

    return !holes.some((hole) => isPointInRing(point, hole))
  })
}

const getImageCoordinates = ({west, south, east, north}) => [
  [west, north],
  [east, north],
  [east, south],
  [west, south],
]

const getRasterSize = (bounds, maxRasterSize = MAX_RASTER_SIZE) => {
  const worldWidth =
    (lonToTileX(bounds.east, TERRAIN_ZOOM) - lonToTileX(bounds.west, TERRAIN_ZOOM)) *
    TERRAIN_TILE_SIZE
  const worldHeight =
    (latToTileY(bounds.south, TERRAIN_ZOOM) - latToTileY(bounds.north, TERRAIN_ZOOM)) *
    TERRAIN_TILE_SIZE
  const aspect = worldWidth > 0 && worldHeight > 0 ? worldWidth / worldHeight : 1

  if (aspect >= 1) {
    return {
      width: maxRasterSize,
      height: Math.max(1, Math.round(maxRasterSize / aspect)),
    }
  }

  return {
    width: Math.max(1, Math.round(maxRasterSize * aspect)),
    height: maxRasterSize,
  }
}

const fetchTerrainTile = async ({x, y, zoom, token}) => {
  const url = `https://api.mapbox.com/v4/mapbox.terrain-rgb/${zoom}/${x}/${y}.pngraw?access_token=${token}`
  const response = await fetch(url)

  if (!response.ok) {
    throw new Error(`No pudimos cargar la tile DEM ${zoom}/${x}/${y}`)
  }

  const blob = await response.blob()
  const bitmap = await createImageBitmap(blob)
  const canvas = document.createElement('canvas')
  canvas.width = TERRAIN_TILE_SIZE
  canvas.height = TERRAIN_TILE_SIZE
  const context = canvas.getContext('2d')
  context.drawImage(bitmap, 0, 0)
  bitmap.close?.()

  return context.getImageData(0, 0, TERRAIN_TILE_SIZE, TERRAIN_TILE_SIZE).data
}

const loadTerrainTiles = async (bounds, token) => {
  const minTileX = Math.floor(lonToTileX(bounds.west, TERRAIN_ZOOM))
  const maxTileX = Math.floor(lonToTileX(bounds.east, TERRAIN_ZOOM))
  const minTileY = Math.floor(latToTileY(bounds.north, TERRAIN_ZOOM))
  const maxTileY = Math.floor(latToTileY(bounds.south, TERRAIN_ZOOM))
  const tileRequests = []

  for (let x = minTileX; x <= maxTileX; x += 1) {
    for (let y = minTileY; y <= maxTileY; y += 1) {
      tileRequests.push(
        fetchTerrainTile({x, y, zoom: TERRAIN_ZOOM, token}).then((data) => [`${x}:${y}`, data])
      )
    }
  }

  return new Map(await Promise.all(tileRequests))
}

const decodeTerrainRgbHeight = (r, g, b) => -10000 + (r * 256 * 256 + g * 256 + b) * 0.1

const mix = (from, to, amount) =>
  from.map((value, index) => Math.round(value + (to[index] - value) * amount))

const getAltimetryColor = (amount) => {
  const stops = [
    {at: 0, color: [18, 8, 34]},
    {at: 0.35, color: [75, 34, 116]},
    {at: 0.68, color: [202, 137, 63]},
    {at: 1, color: [255, 250, 178]},
  ]
  const value = clamp(amount, 0, 1)

  for (let index = 1; index < stops.length; index += 1) {
    const left = stops[index - 1]
    const right = stops[index]

    if (value <= right.at) {
      return mix(left.color, right.color, (value - left.at) / (right.at - left.at))
    }
  }

  return stops[stops.length - 1].color
}

export const ALTIMETRY_LEGEND_WIDTH = 248
export const ALTIMETRY_LEGEND_HEIGHT = 174

const drawRoundedRect = (context, x, y, width, height, radius) => {
  const normalizedRadius = Math.min(radius, width / 2, height / 2)

  context.beginPath()
  context.moveTo(x + normalizedRadius, y)
  context.lineTo(x + width - normalizedRadius, y)
  context.quadraticCurveTo(x + width, y, x + width, y + normalizedRadius)
  context.lineTo(x + width, y + height - normalizedRadius)
  context.quadraticCurveTo(x + width, y + height, x + width - normalizedRadius, y + height)
  context.lineTo(x + normalizedRadius, y + height)
  context.quadraticCurveTo(x, y + height, x, y + height - normalizedRadius)
  context.lineTo(x, y + normalizedRadius)
  context.quadraticCurveTo(x, y, x + normalizedRadius, y)
  context.closePath()
}

const drawFittedText = (
  context,
  text,
  x,
  y,
  maxWidth,
  {color = '#101426', fontSize = 24, fontWeight = 700, minFontSize = 16} = {}
) => {
  let currentFontSize = fontSize
  let output = String(text || '')

  context.fillStyle = color
  context.textAlign = 'left'
  context.textBaseline = 'top'

  while (currentFontSize > minFontSize) {
    context.font = `${fontWeight} ${currentFontSize}px Poppins, Arial, sans-serif`

    if (context.measureText(output).width <= maxWidth) {
      break
    }

    currentFontSize -= 1
  }

  while (context.measureText(output).width > maxWidth && output.length > 1) {
    const nextOutput = output.length > 4 ? `${output.slice(0, -4)}...` : output.slice(0, -1)

    if (nextOutput === output) {
      break
    }

    output = nextOutput
  }

  context.fillText(output, x, y)
}

export const drawAltimetryLegend = (context, legend, x, y, width = ALTIMETRY_LEGEND_WIDTH) => {
  const legendHeight = ALTIMETRY_LEGEND_HEIGHT

  context.save()
  context.shadowColor = 'rgba(15, 23, 42, 0.14)'
  context.shadowBlur = 22
  context.shadowOffsetY = 8
  drawRoundedRect(context, x, y, width, legendHeight, 18)
  context.fillStyle = '#ffffff'
  context.fill()
  context.restore()

  drawRoundedRect(context, x, y, width, legendHeight, 18)
  context.strokeStyle = 'rgba(15, 23, 42, 0.08)'
  context.lineWidth = 1
  context.stroke()

  const contentX = x + 18
  const contentY = y + 18

  context.fillStyle = '#101426'
  context.font = '700 18px Poppins, Arial, sans-serif'
  context.textBaseline = 'top'
  context.textAlign = 'left'
  context.fillText('Leyenda', contentX, contentY)

  context.fillStyle = '#7f87a3'
  context.font = '700 12px Poppins, Arial, sans-serif'
  context.fillText('Altimetr\u00eda del lote (DEM)', contentX, contentY + 30)

  const scaleX = contentX
  const scaleY = contentY + 58
  const scaleWidth = 12
  const scaleHeight = 76
  const gradient = context.createLinearGradient(scaleX, scaleY, scaleX, scaleY + scaleHeight)
  gradient.addColorStop(0, '#fffab2')
  gradient.addColorStop(0.32, '#ca893f')
  gradient.addColorStop(0.65, '#4b2274')
  gradient.addColorStop(1, '#120822')

  drawRoundedRect(context, scaleX, scaleY, scaleWidth, scaleHeight, 999)
  context.fillStyle = gradient
  context.fill()

  context.strokeStyle = 'rgba(15, 23, 42, 0.08)'
  context.lineWidth = 1
  context.stroke()

  const valueRows = [
    {label: 'Mayor altura', value: formatMeters(legend.maxHeight), y: scaleY},
    {label: 'Promedio', value: formatMeters(legend.averageHeight), y: scaleY + 31},
    {label: 'Menor altura', value: formatMeters(legend.minHeight), y: scaleY + 62},
  ]
  const labelX = scaleX + scaleWidth + 12
  const valueX = x + width - 18

  valueRows.forEach((row) => {
    context.fillStyle = '#5f6680'
    context.font = '500 12px Poppins, Arial, sans-serif'
    context.textAlign = 'left'
    context.textBaseline = 'middle'
    context.fillText(row.label, labelX, row.y + 7)

    context.fillStyle = '#101426'
    context.font = '700 12px Poppins, Arial, sans-serif'
    context.textAlign = 'right'
    context.fillText(row.value, valueX, row.y + 7)
  })

  context.textAlign = 'left'

  return legendHeight
}

export const buildAltimetryDownloadCanvas = (
  raster,
  {layerName = 'Altimetr\u00eda del lote (DEM)', subject = null, title = null} = {}
) => {
  const sourceCanvas = raster?.canvas
  const legend = raster?.legend

  if (!sourceCanvas || !legend) {
    return sourceCanvas
  }

  const outerPadding = 24
  const gap = 24
  const legendWidth = ALTIMETRY_LEGEND_WIDTH
  const legendHeight = ALTIMETRY_LEGEND_HEIGHT
  const titleHeight = title ? 58 : 0
  const outputWidth = sourceCanvas.width + gap + legendWidth + outerPadding * 2
  const outputHeight = Math.max(sourceCanvas.height, legendHeight) + outerPadding * 2 + titleHeight
  const outputCanvas = document.createElement('canvas')
  outputCanvas.width = outputWidth
  outputCanvas.height = outputHeight
  const context = outputCanvas.getContext('2d')
  const contentTop = outerPadding + titleHeight
  const sourceY = contentTop + Math.round((outputHeight - titleHeight - outerPadding * 2 - sourceCanvas.height) / 2)
  const legendX = outerPadding + sourceCanvas.width + gap
  const legendY = contentTop + Math.round((outputHeight - titleHeight - outerPadding * 2 - legendHeight) / 2)

  context.fillStyle = '#ffffff'
  context.fillRect(0, 0, outputWidth, outputHeight)

  if (title) {
    drawFittedText(context, title, outerPadding, outerPadding, outputWidth - outerPadding * 2)
  }

  context.drawImage(sourceCanvas, outerPadding, sourceY)
  drawAltimetryLegend(context, legend, legendX, legendY, legendWidth)

  outputCanvas.visualLayerExport = {
    layerCanvas: sourceCanvas,
    layerName,
    legend,
    subject,
    title,
    type: 'altimetry',
  }

  return outputCanvas
}

export const buildAltimetryRaster = async (geometry, options = {}) => {
  const bounds = getGeometryBounds(geometry)

  if (!bounds) {
    throw new Error('El lote no tiene geometria valida para altimetria')
  }

  const token = mapboxgl.accessToken || MAPBOX_TERRAIN_RGB_TOKEN
  const tiles = await loadTerrainTiles(bounds, token)
  const {width, height} = getRasterSize(bounds, options.maxRasterSize || MAX_RASTER_SIZE)
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const context = canvas.getContext('2d')
  const imageData = context.createImageData(width, height)
  const heights = new Float32Array(width * height)
  heights.fill(Number.NaN)

  const minWorldX = lonToTileX(bounds.west, TERRAIN_ZOOM) * TERRAIN_TILE_SIZE
  const maxWorldX = lonToTileX(bounds.east, TERRAIN_ZOOM) * TERRAIN_TILE_SIZE
  const minWorldY = latToTileY(bounds.north, TERRAIN_ZOOM) * TERRAIN_TILE_SIZE
  const maxWorldY = latToTileY(bounds.south, TERRAIN_ZOOM) * TERRAIN_TILE_SIZE
  let minHeight = Infinity
  let maxHeight = -Infinity
  let totalHeight = 0
  let sampleCount = 0

  for (let row = 0; row < height; row += 1) {
    const worldY = minWorldY + (row / Math.max(height - 1, 1)) * (maxWorldY - minWorldY)
    const tileYFloat = worldY / TERRAIN_TILE_SIZE
    const tileY = Math.floor(tileYFloat)
    const pixelY = clamp(Math.floor((tileYFloat - tileY) * TERRAIN_TILE_SIZE), 0, 255)
    const lat = tileYToLat(tileYFloat, TERRAIN_ZOOM)

    for (let col = 0; col < width; col += 1) {
      const worldX = minWorldX + (col / Math.max(width - 1, 1)) * (maxWorldX - minWorldX)
      const tileXFloat = worldX / TERRAIN_TILE_SIZE
      const tileX = Math.floor(tileXFloat)
      const pixelX = clamp(Math.floor((tileXFloat - tileX) * TERRAIN_TILE_SIZE), 0, 255)
      const lon = tileXToLon(tileXFloat, TERRAIN_ZOOM)

      if (!isPointInGeometry([lon, lat], geometry)) {
        continue
      }

      const tile = tiles.get(`${tileX}:${tileY}`)

      if (!tile) {
        continue
      }

      const tileIndex = (pixelY * TERRAIN_TILE_SIZE + pixelX) * 4
      const heightValue = decodeTerrainRgbHeight(
        tile[tileIndex],
        tile[tileIndex + 1],
        tile[tileIndex + 2]
      )
      const rasterIndex = row * width + col
      heights[rasterIndex] = heightValue
      minHeight = Math.min(minHeight, heightValue)
      maxHeight = Math.max(maxHeight, heightValue)
      totalHeight += heightValue
      sampleCount += 1
    }
  }

  if (sampleCount < 1) {
    throw new Error('No pudimos obtener muestras DEM para este lote')
  }

  const range = Math.max(maxHeight - minHeight, 0.1)

  for (let index = 0; index < heights.length; index += 1) {
    const heightValue = heights[index]

    if (!Number.isFinite(heightValue)) {
      continue
    }

    const [r, g, b] = getAltimetryColor((heightValue - minHeight) / range)
    const imageIndex = index * 4
    imageData.data[imageIndex] = r
    imageData.data[imageIndex + 1] = g
    imageData.data[imageIndex + 2] = b
    imageData.data[imageIndex + 3] = 255
  }

  context.putImageData(imageData, 0, 0)

  return {
    canvas,
    bbox: getImageCoordinates(bounds),
    heights,
    width,
    height,
    legend: {
      type: 'altimetry',
      minHeight,
      maxHeight,
      averageHeight: totalHeight / sampleCount,
    },
  }
}

const Altimetry = ({land}) => {
  const mapLayers = useMapLayersV2()
  const mapLegendContext = useMapLegendContext()
  const viewImageContext = useViewImageContext()
  const executing = useRef(false)
  const mapLayersRef = useRef(mapLayers)
  const mapLegendContextRef = useRef(mapLegendContext)
  const viewImageContextRef = useRef(viewImageContext)
  const layerId = `altimetry-${land.land_id}`
  const geometryKey = JSON.stringify(land.polygons)

  mapLayersRef.current = mapLayers
  mapLegendContextRef.current = mapLegendContext
  viewImageContextRef.current = viewImageContext

  useEffect(() => {
    let cancelled = false

    const loadAltimetry = async () => {
      if (executing.current) {
        return
      }

      const cachedRaster = viewImageState.getLandAltimetry(land.land_id, geometryKey)

      if (cachedRaster) {
        mapLayersRef.current.fit(land.land_id)
        mapLayersRef.current.land.addCanvas({
          layerId,
          canvas: cachedRaster.canvas,
          bbox: cachedRaster.bbox,
          opacity: 1,
        })
        mapLegendContextRef.current.changeBasicLegend(land.land_id, cachedRaster.legend)
        return
      }

      executing.current = true
      viewImageContextRef.current.setLoading(true)

      try {
        mapLayersRef.current.fit(land.land_id)
        const raster = await buildAltimetryRaster(land.polygons)

        if (cancelled) {
          return
        }

        mapLayersRef.current.land.addCanvas({
          layerId,
          canvas: raster.canvas,
          bbox: raster.bbox,
          opacity: 1,
        })
        viewImageState.addLandAltimetry(land.land_id, raster, geometryKey)
        mapLegendContextRef.current.changeBasicLegend(land.land_id, raster.legend)
      } catch (error) {
        if (!cancelled) {
          console.error(error)
          mapLayersRef.current.land.deleteImage({
            landId: land.land_id,
            imageType: 'altimetry',
            layerId,
          })
          mapLegendContextRef.current.clearBasicLegend(land.land_id)
          viewImageState.deleteLandAltimetry(land.land_id)
        }
      } finally {
        if (!cancelled) {
          viewImageContextRef.current.setLoading(false)
        }
        executing.current = false
      }
    }

    loadAltimetry()

    return () => {
      cancelled = true
      executing.current = false
      viewImageContextRef.current.setLoading(false)
      mapLegendContextRef.current.clearBasicLegend(land.land_id)
      mapLayersRef.current.land.deleteImage({
        landId: land.land_id,
        imageType: 'altimetry',
        layerId,
      })
    }
  }, [geometryKey, land.land_id, land.polygons, layerId])

  return null
}

export default Altimetry
